import { RetweetOutlined } from '@ant-design/icons';
import { faEdit, faExternalLinkAlt, faRedoAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UISref } from '@uirouter/react';
import { Badge, Button, notification, Popconfirm, Space, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import { FormattedMessage } from 'react-intl';

import { api } from '@/api';
import { DeleteAction } from '@/components/table/actions';
import { PERMISSION_CREATE, PERMISSION_DELETE, PERMISSION_OBJ_DOCUMENT_IMPORT } from '@/constants/permissions';
import { withPermissions } from '@/hocs/permission';
import { boardingImportStore } from '@/stores';
import { showError } from '@/utils/common';
import { getTranslatedString } from '@/utils/intl';

export type SiImportRow =
  cyrian.api.documentImports.DocumentImport<cyrian.api.documentImports.ShippingInstructionParsed>;

const STATUS_COLORS: Record<
  cyrian.api.documentImports.DocumentImportStatus,
  'default' | 'processing' | 'success' | 'warning' | 'error'
> = {
  pending: 'processing',
  parsed: 'processing',
  persisted: 'success',
  review: 'warning',
  failed: 'error',
};

/** Used by the page to decide whether polling should keep firing. */
export const isActive = (status: cyrian.api.documentImports.DocumentImportStatus) =>
  status === 'pending' || status === 'parsed';

const PermissionedDeleteAction = withPermissions([[PERMISSION_OBJ_DOCUMENT_IMPORT, PERMISSION_DELETE]])(DeleteAction);

// Same 360px cap as waybill-imports: without it the long confirm copy
// stretches the Popconfirm overlay over most of the table.
const POPCONFIRM_OVERLAY_STYLE = { maxWidth: 360 };

// Review opens the editable drawer; approve there hits POST
// /document-imports/:id/retry (gated by Create on the backend). Hide the
// button from readonly users — they couldn't submit from the drawer anyway.
const ReviewButton = withPermissions([[PERMISSION_OBJ_DOCUMENT_IMPORT, PERMISSION_CREATE]])(
  ({ onClick }: { onClick: () => void }) => (
    <Tooltip title={getTranslatedString('boardings.imports.actions.review')}>
      <Button type="primary" shape="circle" icon={<FontAwesomeIcon icon={faEdit} />} onClick={onClick} />
    </Tooltip>
  ),
);

const ForceReparseButton = withPermissions([[PERMISSION_OBJ_DOCUMENT_IMPORT, PERMISSION_CREATE]])(
  ({ onConfirm, confirmKey, loading }: { onConfirm: () => void; confirmKey: string; loading: boolean }) => (
    <Popconfirm
      title={getTranslatedString(confirmKey)}
      onConfirm={onConfirm}
      onCancel={(e) => e?.stopPropagation()}
      disabled={loading}
      overlayStyle={POPCONFIRM_OVERLAY_STYLE}
    >
      <Tooltip title={getTranslatedString('boardings.imports.actions.force-reparse')}>
        <Button
          type="primary"
          shape="circle"
          icon={<FontAwesomeIcon icon={faRedoAlt} />}
          loading={loading}
          onClick={(e) => e.stopPropagation()}
        />
      </Tooltip>
    </Popconfirm>
  ),
);

const onDelete = (id: number) => {
  api.documentImports
    .delete(id)
    .source.then(() => boardingImportStore.deleteItem(id))
    .catch((err: any) => {
      // Network/timeout errors carry no response — convert them into a
      // friendly warning hinting at the recovery action (refresh) instead
      // of a scary axios message that doesn't say whether the delete
      // actually went through server-side.
      const isTimeout = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message ?? '');
      const isNetwork = !err?.response && !isTimeout;
      if (isTimeout || isNetwork) {
        notification.warning({
          message: getTranslatedString('boardings.imports.delete.unreachable'),
          duration: 8,
        });
        return;
      }
      showError(err);
    });
};

export interface ColumnsCallbacks {
  /** Opens the Review drawer for a `review`-status row. The drawer pre-fills
   *  from `parsedData`, collects the operator approve-fields and POSTs the
   *  edited values back through retry. */
  onReview: (row: SiImportRow) => void;
  /** Resubmits OCR on the stored source document as a new import row — fresh
   *  Gemini call; existing rows are kept and refreshed with the new result.
   *  Spends Gemini quota. */
  onForceReparse: (id: number) => void;
  /** Id of the row whose force-reparse POST is in flight (null when idle).
   *  Drives per-row spinner + disables Popconfirm to block double-click. */
  forceReparsingId: number | null;
}

export const getColumns = ({
  onReview,
  onForceReparse,
  forceReparsingId,
}: ColumnsCallbacks): ColumnsType<SiImportRow> => [
  {
    title: <FormattedMessage id="boardings.imports.column.file" />,
    dataIndex: 'fileName',
    key: 'fileName',
    ellipsis: true,
    render: (fileName: string, record: SiImportRow) => {
      if (!record.reusedFromImportId) {
        return fileName;
      }
      // <span> (not <div>): antd wraps ellipsis cells in a <span>; a
      // block child would be invalid HTML5 here.
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <Typography.Text ellipsis={{ tooltip: fileName }} style={{ flex: 1, minWidth: 0 }}>
            {fileName}
          </Typography.Text>
          <Tooltip title={getTranslatedString('boardings.imports.badge.reused', { id: record.reusedFromImportId })}>
            <RetweetOutlined />
          </Tooltip>
        </span>
      );
    },
  },
  {
    title: <FormattedMessage id="boardings.imports.column.si-number" />,
    key: 'siNumber',
    width: 160,
    ellipsis: true,
    render: (_: unknown, record: SiImportRow) => record.parsedData?.siNumber ?? null,
  },
  {
    title: <FormattedMessage id="boardings.imports.column.status" />,
    key: 'status',
    width: 140,
    render: (_: unknown, record: SiImportRow) => (
      <Badge
        status={STATUS_COLORS[record.status]}
        text={<FormattedMessage id={`boardings.imports.status.${record.status}`} />}
      />
    ),
  },
  {
    title: <FormattedMessage id="boardings.imports.column.created" />,
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 180,
    render: (createdAt: string) => (createdAt ? new Date(createdAt).toLocaleString() : ''),
  },
  {
    title: <FormattedMessage id="boardings.imports.column.result" />,
    key: 'result',
    // Reserve fixed width and let the cell ellipsize so a long error
    // message can't overlap the Actions cell.
    width: 360,
    ellipsis: { showTitle: false },
    render: (_: unknown, row: SiImportRow) => {
      if (row.status === 'persisted' && row.targetEntityId) {
        return (
          <UISref to="base-layout.boardings.edit" params={{ boardingId: row.targetEntityId }}>
            {/* biome-ignore lint/a11y/useValidAnchor: UISref injects href onto the child anchor at runtime */}
            {/* biome-ignore lint/a11y/noStaticElementInteractions: see above — this renders as a real link */}
            <a onClick={(e) => e.stopPropagation()}>
              <FontAwesomeIcon icon={faExternalLinkAlt} />{' '}
              <FormattedMessage id="boardings.imports.result.boarding-link" values={{ id: row.targetEntityId }} />
            </a>
          </UISref>
        );
      }
      if (row.status === 'review' && row.amendmentOfBoardingId != null) {
        return (
          <Tooltip
            title={getTranslatedString('boardings.imports.result.amendment-tooltip', {
              id: row.amendmentOfBoardingId,
            })}
          >
            <Tag color="orange">
              <FormattedMessage id="boardings.imports.result.amendment" values={{ id: row.amendmentOfBoardingId }} />
            </Tag>
          </Tooltip>
        );
      }
      if (row.warnings && row.warnings.length > 0) {
        return (
          <Tooltip title={row.warnings.join('\n')}>
            <Tag color="orange">
              <FormattedMessage id="boardings.imports.result.warnings" values={{ count: row.warnings.length }} />
            </Tag>
          </Tooltip>
        );
      }
      if (row.error) {
        return (
          <Tooltip title={row.error} placement="topLeft">
            <Typography.Text type="danger" ellipsis style={{ maxWidth: '100%' }}>
              {row.error}
            </Typography.Text>
          </Tooltip>
        );
      }
      return null;
    },
  },
  {
    // Visible (not hover-revealed) actions on purpose — this is a task
    // queue, not a CRUD list; same divergence as waybill-imports.
    title: <FormattedMessage id="boardings.imports.column.actions" />,
    key: 'actions',
    width: 150,
    align: 'right',
    render: (_: unknown, record: SiImportRow) => (
      <Space onClick={(e) => e.stopPropagation()}>
        {record.status === 'review' && <ReviewButton onClick={() => onReview(record)} />}
        {/* Confirm text branches: only `persisted` has a boarding that a
            fresh parse+approve would overwrite. */}
        {(record.status === 'persisted' || record.status === 'review' || record.status === 'failed') && (
          <ForceReparseButton
            onConfirm={() => onForceReparse(record.id)}
            confirmKey={
              record.status === 'persisted'
                ? 'boardings.imports.actions.force-reparse-confirm-persisted'
                : 'boardings.imports.actions.force-reparse-confirm-other'
            }
            loading={forceReparsingId === record.id}
          />
        )}
        {/* No Re-apply here: backend reapply() supports waybill_pdf only —
            an SI re-persist is the approve (retry) flow instead. Delete is
            limited to terminal states for the same reasons as waybill-imports
            (pending/parsed race the OCR worker; persisted = delete the
            boarding, not the import). */}
        {(record.status === 'review' || record.status === 'failed') && (
          <PermissionedDeleteAction onConfirm={() => onDelete(record.id)} />
        )}
      </Space>
    ),
  },
];

// Convenience for non-render consumers (`getGenericFilters` introspects the
// column descriptors at module-load time and doesn't need any callbacks).
export const columns: ColumnsType<SiImportRow> = getColumns({
  onReview: () => undefined,
  onForceReparse: () => undefined,
  forceReparsingId: null,
});
